import React, { useEffect } from 'react';
import {
  Stack,
  InputAdornment,
  FormControlLabel,
  Switch,
  Box
} from '@mui/material';
import {
  Person as PersonIcon,
  CheckCircle as ActiveIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { EasyCheckDialog, EasyCheckTextField, EasyCheckButton } from '../../common/Controls';
import PropTypes from 'prop-types';

const BeneficiaryFormModal = ({ 
  open, 
  beneficiary, 
  onClose, 
  onSubmit,
  loading = false 
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      beneficiaryName: '',
      isActive: true
    }
  });

  useEffect(() => {
    if (beneficiary) {
      reset({
        beneficiaryName: beneficiary.beneficiaryName || '',
        isActive: beneficiary.isActive !== false
      });
    } else {
      reset({
        beneficiaryName: '',
        isActive: true
      });
    }
  }, [beneficiary, reset, open]);

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <EasyCheckDialog
      open={open}
      onClose={handleClose}
      title={beneficiary ? 'Edit Beneficiary' : 'Add Beneficiary'}
      variant="form"
      titleIcon={<PersonIcon />}
      actions={
        <>
          <EasyCheckButton
            variant="outlined"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </EasyCheckButton>
          <EasyCheckButton
            type="submit"
            form="beneficiary-form"
            loading={loading}
            variant="contained"
          >
            {beneficiary ? 'Update Beneficiary' : 'Add Beneficiary'}
          </EasyCheckButton>
        </>
      }
    >
      <Box
        component="form"
        id="beneficiary-form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ width: '100%' }}
      >
        <Stack spacing={2} sx={{ mt: 4 }}>
          <Controller
            name="beneficiaryName"
            control={control}
            rules={{ 
              required: 'Beneficiary name is required',
              minLength: {
                value: 2,
                message: 'Beneficiary name should be at least 2 characters'
              },
              maxLength: {
                value: 100,
                message: 'Beneficiary name should not exceed 100 characters'
              }
            }}
            render={({ field }) => (
              <EasyCheckTextField
                {...field}
                variant="form"
                label="Beneficiary Name"
                error={!!errors.beneficiaryName}
                helperText={errors.beneficiaryName?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="primary" />
                    </InputAdornment>
                  ),
                }}
                placeholder="Enter beneficiary name"
                autoFocus
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0,
                  },
                }}
              />
            )}
          />

          
          <Controller
            name="isActive"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={value}
                    onChange={(e) => onChange(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ActiveIcon color={value ? "success" : "disabled"} />
                    <span>{value ? 'Active' : 'Inactive'}</span>
                  </Stack>
                }
                sx={{ alignSelf: 'flex-start', mt: 1 }}
              />
            )}
          />
        </Stack>
      </Box>
    </EasyCheckDialog>
  );
};

BeneficiaryFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  beneficiary: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

export default BeneficiaryFormModal; 